import React from "react";

/**
 * Global React Error Boundary.
 *
 * Prevents uncaught render/lifecycle errors (e.g. "Objects are not valid as a
 * React child") from unmounting the entire tree and leaving the user staring
 * at a blank/black screen. Shows a safe, on-brand fallback with a Reload +
 * Go Home affordance.
 *
 * This is defense-in-depth ONLY — real bugs should still be fixed at the source.
 * We wire this at the root of App.js so it catches errors from anywhere in the
 * component tree.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message:
        (error && (error.message || String(error))) ||
        "Something went wrong rendering this page.",
    };
  }

  componentDidCatch(error, info) {
    // Log to the console so devs / support can grep for it in production
    // (Sentry / LogRocket wiring is intentionally deferred — user has not
    // requested a specific error reporter yet).
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: "" });
  };

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  handleGoHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="global-error-boundary"
        className="min-h-screen bg-background text-foreground flex items-center justify-center px-6"
      >
        <div className="max-w-md w-full text-center space-y-5">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-destructive/40 bg-destructive/10 text-destructive"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" x2="12" y1="9" y2="13" />
              <line x1="12" x2="12.01" y1="17" y2="17" />
            </svg>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              Something went wrong
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              We hit an unexpected problem. Your account is safe — nothing was
              lost. Try reloading, or head back to the home page.
            </p>
            {this.state.message ? (
              <p
                data-testid="error-boundary-message"
                className="mt-3 text-xs font-mono text-muted-foreground/80 break-all"
              >
                {String(this.state.message).slice(0, 240)}
              </p>
            ) : null}
          </div>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              data-testid="error-boundary-reload"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 text-sm font-medium transition-colors"
            >
              Reload page
            </button>
            <button
              type="button"
              data-testid="error-boundary-home"
              onClick={this.handleGoHome}
              className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 text-sm font-medium transition-colors"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
